import React, {useContext, useState, useEffect} from 'react';
import {
    Grid,
    Container,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    makeStyles,
    CircularProgress,
    Box,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import {useTranslation} from "react-i18next";
import Page from 'components/Page';
import AppContext from 'context/AppContext';
import SecureFetchContext from 'context/SecureFetchContext';
import getApiRoute from 'routes/apiRoutes';
import {isAdmin} from 'helpers';

const useStyles = makeStyles((theme) => ({
    root: {},
    filterRow: {
        marginBottom: theme.spacing(3),
    },
    table: {
        '& .MuiTableCell-root': {
            padding: '8px 12px',
        },
    },
    tableHeader: {
        backgroundColor: '#ec4f30',
        color: '#ffffff',
        fontWeight: 500,
        fontSize: '0.875rem',
        textAlign: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        padding: '8px 12px',
        lineHeight: 1.4,
        borderBottom: 'none',
    },
    tableSubHeader: {
        backgroundColor: '#ffffff',
        fontSize: '0.85rem',
        textAlign: 'center',
        position: 'sticky',
        top: '36px',
        zIndex: 99,
        padding: '8px 12px',
        lineHeight: 1.3,
        borderTop: 'none',
    },
    dayCell: {
        fontWeight: 600,
        textAlign: 'center',
    },
    totalRow: {
        backgroundColor: theme.palette.grey[100],
        fontWeight: 'bold',
        borderTop: '2px solid ' + theme.palette.grey[400],
        '& .MuiTableCell-root': {
            fontWeight: 'bold',
        },
    },
    emptyRow: {
        opacity: 0.4,
    },
    rowWithData: {
        '& .MuiTableCell-root:not(:first-child)': {
            fontWeight: 'bold',
        },
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
    },
    cellContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
    },
    percentValue: {
        fontSize: '0.95rem',
    },
    countValue: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    cellAttempt: {
        backgroundColor: '#fce8e9', // Červená pre pokusy
    },
    cellDelivered: {
        backgroundColor: '#e8f5e9', // Zelená pre doručené
    },
    cellNone: {
        backgroundColor: 'transparent',
    },
    tableWrapper: {},
    tableContainer: {
        maxHeight: 'calc(100vh - 380px)',
    },
}));

export default function DeliverySpeedStatistics() {
    const classes = useStyles();
    const {t} = useTranslation();
    const {appValues} = useContext(AppContext);
    const secureFetch = useContext(SecureFetchContext);

    // Current date for default month
    const currentDate = new Date();
    const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

    // Filters
    const [month, setMonth] = useState(currentMonth);
    const [userId, setUserId] = useState('');
    const [shipperId, setShipperId] = useState('');
    const [deliveryType, setDeliveryType] = useState('address');
    const [country, setCountry] = useState('');

    // Data
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Lists for filters
    const [users, setUsers] = useState([]);
    const [shippers, setShippers] = useState([]);
    const [countries, setCountries] = useState([]);
    const [availableCountries, setAvailableCountries] = useState([]);

    const admin = isAdmin(appValues.user);

    // Generate list of months (last 24 months)
    const getMonthOptions = () => {
        const months = [];
        const now = new Date();
        for (let i = 0; i < 24; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            months.push({
                value,
                label: date.toLocaleDateString('sk-SK', {year: 'numeric', month: 'long'})
            });
        }
        return months;
    };

    // Farebná škála pre percentá
    const getColorClass = (percent, type) => {
        if (percent === 0 || !percent) return classes.cellNone;
        if (type === 'attempt') return classes.cellAttempt; // Červená pre pokusy
        if (type === 'delivered') return classes.cellDelivered; // Zelená pre doručené
        return classes.cellNone;
    };

    // Formátovanie percenta
    const formatPercent = (value) => {
        if (typeof value !== 'number' || value === 0) return '0,00%';
        return value.toFixed(2).replace('.', ',') + '%';
    };

    // Load filter options
    useEffect(() => {
        // Load shippers
        secureFetch(getApiRoute('shippers')).then(json => {
            setShippers(json || []);
            // Automaticky vyber prvého prepravcu z filtrovaného zoznamu (SPS = 2)
            const allowedShippers = [2, 1, 3];
            const filtered = (json || []).filter(s => allowedShippers.includes(s.id));
            if (filtered.length > 0) {
                const sorted = filtered.sort((a, b) => allowedShippers.indexOf(a.id) - allowedShippers.indexOf(b.id));
                setShipperId(sorted[0].id.toString());
            }
        }).catch(err => {
            console.error('Error loading shippers:', err);
        });

        // Load countries
        secureFetch(getApiRoute('states')).then(json => {
            setCountries(json || []);
        }).catch(err => {
            console.error('Error loading countries:', err);
        });

        // Load users (Admin only)
        if (admin) {
            secureFetch(getApiRoute('admin_user')).then(json => {
                setUsers(json || []);
            }).catch(err => {
                console.error('Error loading users:', err);
            });
        }
    }, [admin, secureFetch]);

    // Load statistics data
    useEffect(() => {
        const loadData = async () => {
            if (!month || !shipperId) {
                return;
            }

            setLoading(true);
            setError(null);

            try {
                // Build query params
                const params = new URLSearchParams({
                    month,
                    shipperId,
                    deliveryType,
                });

                if (country) {
                    params.append('country', country);
                }

                if (admin && userId) {
                    params.append('userId', userId);
                }

                // Choose endpoint based on role
                const endpoint = admin
                    ? `${getApiRoute('admin_statistics')}deliveryspeed?${params.toString()}`
                    : `${getApiRoute('statistics')}deliveryspeed?${params.toString()}`;

                const result = await secureFetch(endpoint);
                setData(result);

                // Update available countries from response
                if (result.available_countries) {
                    setAvailableCountries(result.available_countries);
                }
            } catch (err) {
                setError(err.message || 'Chyba pri načítaní dát');
                console.error('Error loading data:', err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [month, userId, shipperId, deliveryType, country, admin, secureFetch]);

    return (
        <Page title={t('navbar.delivery_speed')}>
            <Container maxWidth={false}>
                <Typography variant="h1" gutterBottom>
                    Štatistika rýchlosti doručenia
                </Typography>

                {/* Filters */}
                <Card className={classes.filterRow}>
                    <CardContent>
                        <Grid container spacing={3}>
                            {/* User (Admin only) */}
                            {admin && (
                                <Grid item xs={12} sm={6} lg style={{minWidth: 200, flexGrow: 1}}>
                                    <FormControl fullWidth>
                                        <InputLabel>Názov eshopu</InputLabel>
                                        <Select
                                            value={userId}
                                            onChange={(e) => setUserId(e.target.value)}
                                        >
                                            <MenuItem value="">Všetci</MenuItem>
                                            {users.map(user => (
                                                <MenuItem key={user.id} value={user.id}>
                                                    {user.eshop || `User #${user.id}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* Month */}
                            <Grid item xs={12} sm={6} lg style={{minWidth: 200, flexGrow: 1}}>
                                <FormControl fullWidth>
                                    <InputLabel>Prijaté do prepravy</InputLabel>
                                    <Select
                                        value={month}
                                        onChange={(e) => setMonth(e.target.value)}
                                    >
                                        {getMonthOptions().map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Shipper */}
                            <Grid item xs={12} sm={6} lg style={{minWidth: 200, flexGrow: 1}}>
                                <FormControl fullWidth>
                                    <InputLabel>Prepravca</InputLabel>
                                    <Select
                                        value={shipperId}
                                        onChange={(e) => setShipperId(e.target.value)}
                                    >
                                        {shippers
                                            .filter(s => [2, 1, 3].includes(s.id))
                                            .sort((a, b) => [2, 1, 3].indexOf(a.id) - [2, 1, 3].indexOf(b.id))
                                            .map(shipper => (
                                                <MenuItem key={shipper.id} value={shipper.id}>
                                                    {shipper.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Delivery Type */}
                            <Grid item xs={12} sm={6} lg style={{minWidth: 200, flexGrow: 1}}>
                                <FormControl fullWidth>
                                    <InputLabel>Typ doručenia</InputLabel>
                                    <Select
                                        value={deliveryType}
                                        onChange={(e) => setDeliveryType(e.target.value)}
                                    >
                                        <MenuItem value="address">Doručenie na adresu</MenuItem>
                                        <MenuItem value="pickup">Doručenie na výdajné miesto</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Country */}
                            <Grid item xs={12} sm={6} lg style={{minWidth: 200, flexGrow: 1}}>
                                <FormControl fullWidth>
                                    <InputLabel>Štát do</InputLabel>
                                    <Select
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                    >
                                        <MenuItem value="">Všetky</MenuItem>
                                        {countries
                                            .filter(c => availableCountries.length === 0 || availableCountries.includes(c.code))
                                            .map(c => (
                                                <MenuItem key={c.code} value={c.code}>
                                                    {c.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Loading */}
                {loading && (
                    <div className={classes.loadingContainer}>
                        <CircularProgress/>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <Alert severity="error" style={{marginBottom: 16}}>
                        {error}
                    </Alert>
                )}

                {/* Data Table */}
                {!loading && data && (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            {data.base_total > 0 ? (
                                <Card>
                                    <CardContent>
                                        <div className={classes.tableWrapper}>
                                            <TableContainer className={classes.tableContainer}>
                                                <Table className={classes.table} size="small" stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell className={classes.tableHeader} rowSpan={2}>
                                                                Deň<br/>prijatia do<br/>prepravy
                                                            </TableCell>
                                                            <TableCell className={classes.tableHeader} rowSpan={2}>
                                                                Počet balíkov
                                                            </TableCell>
                                                            {['D+1', 'D+2', 'D+3', 'D+4', 'D+5'].map((label) => (
                                                                <TableCell key={label} className={classes.tableHeader}
                                                                           colSpan={2}>
                                                                    {label}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                        <TableRow>
                                                            {['d1', 'd2', 'd3', 'd4', 'd5'].map((d) => (
                                                                <React.Fragment key={d}>
                                                                    <TableCell className={classes.tableSubHeader}>
                                                                        % balíkov<br/> s pokusom <br/> o doručenie
                                                                    </TableCell>
                                                                    <TableCell className={classes.tableSubHeader}>
                                                                        % doručených<br/>balíkov
                                                                    </TableCell>
                                                                </React.Fragment>
                                                            ))}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {data.rows && data.rows.map((row, index) => {
                                                            const isTotalRow = row.day === 'TOTAL';
                                                            const hasData = row.total_packages > 0;

                                                            return (
                                                                <TableRow
                                                                    key={`row-${row.day}`}
                                                                    className={`${isTotalRow ? classes.totalRow : ''} ${index % 2 && !isTotalRow ? 'rowbg' : ''} ${hasData && !isTotalRow ? classes.rowWithData : ''}`}
                                                                >
                                                                    <TableCell className={classes.dayCell}>
                                                                        {row.day === 'TOTAL' ? 'CELKOM' : row.day}
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        {row.total_packages || 0}
                                                                    </TableCell>
                                                                    {['d1', 'd2', 'd3', 'd4', 'd5'].map((d) => {
                                                                        const dayData = row[d] || {};
                                                                        const pokusPct = dayData.pokus_pct || 0;
                                                                        const pokusCount = dayData.pokus_count || 0;
                                                                        const dorucenePct = dayData.dorucene_pct || 0;
                                                                        const doruceneCount = dayData.dorucene_count || 0;
                                                                    
                                                                        return (
                                                                            <React.Fragment key={`${row.day}-${d}`}>
                                                                                {/* Pokus column */}
                                                                                <TableCell
                                                                                    align="center"
                                                                                    className={getColorClass(pokusPct, 'attempt')}
                                                                                >
                                                                                    <Box
                                                                                        className={classes.cellContent}>
                                                                                    <span
                                                                                        className={classes.percentValue}>
                                                                                        {formatPercent(pokusPct)}
                                                                                    </span>
                                                                                        {pokusCount > 0 && (
                                                                                            <span
                                                                                                className={classes.countValue}>
                                                                                            ({pokusCount})
                                                                                        </span>
                                                                                        )}
                                                                                    </Box>
                                                                                </TableCell>
                                                                                {/* Doručené column */}
                                                                                <TableCell
                                                                                    align="center"
                                                                                    className={getColorClass(dorucenePct, 'delivered')}
                                                                                >
                                                                                    <Box
                                                                                        className={classes.cellContent}>
                                                                                    <span
                                                                                        className={classes.percentValue}>
                                                                                        {formatPercent(dorucenePct)}
                                                                                    </span>
                                                                                        {doruceneCount > 0 && (
                                                                                            <span
                                                                                                className={classes.countValue}>
                                                                                            ({doruceneCount})
                                                                                        </span>
                                                                                        )}
                                                                                    </Box>
                                                                                </TableCell>
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </div>
                                    </CardContent>
                                </Card>
                            ) : (
                                <Alert severity="info">
                                    Pre zvolené kritériá nie sú k dispozícii žiadne dáta.
                                </Alert>
                            )}
                        </Grid>
                    </Grid>
                )}

                {/* Info */}
                {!loading && data && data.base_total > 0 && (
                    <Alert severity="info" style={{marginTop: 16}}>
                        <strong>Poznámka:</strong> Tabuľka zobrazuje percentuálne zastúpenie balíkov, ktoré mali pokus o
                        doručenie
                        alebo boli doručené v príslušnom počte dní (D+1 až D+5) od dňa prijatia do prepravy.
                        Riadok "CELKOM" obsahuje súhrnné štatistiky za celý mesiac.
                    </Alert>
                )}
            </Container>
        </Page>
    );
}
